import { useEffect, useState } from "react";
import { getStoredDemoExpenses, getStoredDemoIncome } from "../utils/demoStorage";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { BASE_URL } from "../utils/apiConfig";
import { buildMonthYearQuery, getMonthName } from "../utils/monthYear";

function Reports({ selectedMonth, selectedYear }) {
  const [demoMode, setDemoMode] = useState(() => getIsDemoMode());
  const [expenses, setExpenses] = useState([]);
  const [incomeList, setIncomeList] = useState([]);

  const format = (num) => Number(num || 0).toLocaleString("en-IN");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const loadReports = async () => {
    if (demoMode) {
      setExpenses(getStoredDemoExpenses(selectedMonth, selectedYear));
      setIncomeList(getStoredDemoIncome(selectedMonth, selectedYear));
      return;
    }

    try {
      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const expenseRes = await fetch(`${BASE_URL}/api/expenses?${query}`, {
        headers: getAuthHeaders(),
      });

      const expenseData = await expenseRes.json();

      const incomeRes = await fetch(`${BASE_URL}/api/income?${query}`, {
        headers: getAuthHeaders(),
      });

      const incomeData = await incomeRes.json();

      setExpenses(Array.isArray(expenseData) ? expenseData : []);
      setIncomeList(Array.isArray(incomeData) ? incomeData : []);
    } catch {
      setExpenses([]);
      setIncomeList([]);
    }
  };

  useEffect(() => {
    setDemoMode(getIsDemoMode());
  }, []);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, demoMode]);

  const exportExcel = async () => {
    try {
      if (demoMode) {
        const XLSX = await import("xlsx");
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(
          expenses.map((expense) => ({
            Date: expense.createdAt
              ? new Date(expense.createdAt).toLocaleDateString("en-IN")
              : `${getMonthName(selectedMonth)} ${selectedYear}`,
            Description: expense.text || expense.description || "Expense",
            Category: expense.category || "others",
            Amount: Number(expense.amount || 0),
            Source: "demo",
          }))
        );

        XLSX.utils.book_append_sheet(workbook, worksheet, "Demo Expenses");
        XLSX.writeFile(workbook, "finora-demo-expenses.xlsx");
        return;
      }

      const token = localStorage.getItem("token");

      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const response = await fetch(`${BASE_URL}/api/expenses/export/excel?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Excel export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "expenses.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed to export Excel");
    }
  };

  const totalIncome = incomeList.reduce(
    (sum, income) => sum + Number(income.amount || 0),
    0
  );

  const totalExpense = expenses.reduce(
    (sum, expense) => sum + Number(expense.amount || 0),
    0
  );

  const balance = totalIncome - totalExpense;

  const categorySummary = Object.entries(
    expenses.reduce((acc, expense) => {
      const category = (expense.category || "others").toLowerCase();
      acc[category] = (acc[category] || 0) + Number(expense.amount || 0);
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  const monthlySummary = Object.entries(
    expenses.reduce((acc, expense) => {
      const date = new Date(expense.createdAt);
      const month = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      acc[month] = (acc[month] || 0) + Number(expense.amount || 0);
      return acc;
    }, {})
  ).sort((a, b) => b[0].localeCompare(a[0]));

  const highestCategory = categorySummary[0];
  const maxCategoryTotal = Math.max(
    ...categorySummary.map(([, total]) => Number(total || 0)),
    0
  );
  const maxMonthlyTotal = Math.max(
    ...monthlySummary.map(([, total]) => Number(total || 0)),
    0
  );
  const savings = Math.max(balance, 0);
  const incomeExpenseMax = Math.max(totalIncome, totalExpense, 1);
  const averageExpense =
    expenses.length > 0 ? totalExpense / expenses.length : 0;
  const latestMonthlyTotal = Number(monthlySummary[0]?.[1] || 0);
  const previousMonthlyTotal = Number(monthlySummary[1]?.[1] || 0);
  const monthlyTrend =
    monthlySummary.length < 2
      ? "More monthly data needed"
      : latestMonthlyTotal > previousMonthlyTotal
      ? "Spending increased"
      : latestMonthlyTotal < previousMonthlyTotal
      ? "Spending decreased"
      : "Spending stayed flat";

  let reportInsight =
    "Add more expenses to generate stronger report insights.";

  if (highestCategory && totalExpense > 0) {
    const percent = ((highestCategory[1] / totalExpense) * 100).toFixed(1);

    reportInsight = `${highestCategory[0]} is your highest spending category at \u20b9${format(
      highestCategory[1]
    )}, which is ${percent}% of total expenses.`;
  }

  return (
    <div className="page reports-premium-page">
      <div className="reports-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">Financial Analytics</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode</span>}
          </div>
          <h2>Reports</h2>
          <p>
            {demoMode
              ? "Analyze sample spending, income, savings, and trends."
              : "Analyze monthly spending, income, savings, and trends"}
          </p>
        </div>

        <button type="button" className="glow-button" onClick={exportExcel}>
          Export Excel
        </button>
      </div>

      {demoMode && (
        <div className="demo-mode-notice">
          Demo Mode - Sample Data. Excel export uses local demo expenses only.
        </div>
      )}

      <div className="reports-summary-grid">
        <div className="report-summary-card income">
          <span>Total Income</span>
          <strong>{"\u20b9"}{format(totalIncome)}</strong>
          <p>{incomeList.length} income entries</p>
        </div>

        <div className="report-summary-card expense">
          <span>Total Expenses</span>
          <strong>{"\u20b9"}{format(totalExpense)}</strong>
          <p>{expenses.length} expense entries</p>
        </div>

        <div className="report-summary-card savings">
          <span>Savings</span>
          <strong>{"\u20b9"}{format(savings)}</strong>
          <p>{balance >= 0 ? "Available after expenses" : "No savings this month"}</p>
        </div>

        <div className="report-summary-card balance">
          <span>Balance / Remaining</span>
          <strong className={balance >= 0 ? "positive" : "negative"}>
            {"\u20b9"}{format(balance)}
          </strong>
          <p>
            {getMonthName(selectedMonth)} {selectedYear}
          </p>
        </div>
      </div>

      <div className="reports-main-grid">
        <div className="reports-left-column">
          <div className="premium-panel report-panel monthly-overview-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Monthly Overview</span>
                <h3>Monthly Spending Chart</h3>
              </div>
              <p>{"\u20b9"}{format(totalExpense)}</p>
            </div>

            {monthlySummary.length === 0 ? (
              <p className="placeholder-text">No monthly data available.</p>
            ) : (
              <div className="report-chart-list">
                {monthlySummary.map(([month, total]) => {
                  const percentage =
                    maxMonthlyTotal > 0
                      ? (Number(total || 0) / maxMonthlyTotal) * 100
                      : 0;

                  return (
                    <div key={month} className="report-chart-row">
                      <div className="report-chart-row-top">
                        <span>{month}</span>
                        <strong>{"\u20b9"}{format(total)}</strong>
                      </div>
                      <div className="report-bar-track">
                        <div
                          className="report-bar-fill spending"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="premium-panel report-panel trend-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Spending Trend</span>
                <h3>Income vs Expense</h3>
              </div>
            </div>

            <div className="income-expense-chart">
              <div className="report-chart-row">
                <div className="report-chart-row-top">
                  <span>Income</span>
                  <strong>{"\u20b9"}{format(totalIncome)}</strong>
                </div>
                <div className="report-bar-track">
                  <div
                    className="report-bar-fill income"
                    style={{ width: `${(totalIncome / incomeExpenseMax) * 100}%` }}
                  />
                </div>
              </div>

              <div className="report-chart-row">
                <div className="report-chart-row-top">
                  <span>Expenses</span>
                  <strong>{"\u20b9"}{format(totalExpense)}</strong>
                </div>
                <div className="report-bar-track">
                  <div
                    className="report-bar-fill expense"
                    style={{ width: `${(totalExpense / incomeExpenseMax) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="premium-panel report-panel analytics-summary-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Analytics Summary</span>
                <h3>Report Signals</h3>
              </div>
            </div>

            <div className="report-signal-grid">
              <div className="report-signal-card">
                <span>Highest Category</span>
                <strong>{highestCategory?.[0] || "None"}</strong>
                <p>
                  {highestCategory
                    ? `\u20b9${format(highestCategory[1])}`
                    : "No category data"}
                </p>
              </div>

              <div className="report-signal-card">
                <span>Average Expense</span>
                <strong>{"\u20b9"}{format(averageExpense)}</strong>
                <p>{expenses.length} transactions</p>
              </div>

              <div className="report-signal-card">
                <span>Monthly Trend</span>
                <strong>{monthlyTrend}</strong>
                <p>
                  {monthlySummary.length >= 2
                    ? `${monthlySummary[0][0]} vs ${monthlySummary[1][0]}`
                    : `${getMonthName(selectedMonth)} ${selectedYear}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="reports-right-column">
          <div className="premium-panel report-panel category-analysis-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Category Analysis</span>
                <h3>Category Breakdown</h3>
              </div>
              <p>{categorySummary.length} categories</p>
            </div>

            {categorySummary.length === 0 ? (
              <p className="placeholder-text">No category data available.</p>
            ) : (
              <div className="report-category-list">
                {categorySummary.map(([category, total], index) => {
                  const percentage =
                    maxCategoryTotal > 0
                      ? (Number(total || 0) / maxCategoryTotal) * 100
                      : 0;

                  return (
                    <div
                      key={category}
                      className={`report-category-row ${index === 0 ? "top" : ""}`}
                    >
                      <div className="report-chart-row-top">
                        <span>{category}</span>
                        <strong>{"\u20b9"}{format(total)}</strong>
                      </div>
                      <div className="report-bar-track">
                        <div
                          className="report-bar-fill category"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="premium-panel report-panel top-categories-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Top Spending</span>
                <h3>Top Categories</h3>
              </div>
            </div>

            {categorySummary.length === 0 ? (
              <p className="placeholder-text">No top categories yet.</p>
            ) : (
              <div className="top-category-stack">
                {categorySummary.slice(0, 3).map(([category, total], index) => (
                  <div key={category} className="top-category-card">
                    <span>#{index + 1}</span>
                    <strong>{category}</strong>
                    <p>{"\u20b9"}{format(total)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="premium-panel report-panel insights-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Report Insights</span>
                <h3>Insights</h3>
              </div>
            </div>

            <ul className="premium-insights-list">
              <li>{reportInsight}</li>
              <li>Monthly Summary helps you compare spending month by month.</li>
              <li>
                {demoMode
                  ? "Excel export downloads a demo-only expense report."
                  : "Excel export downloads your expense report."}
              </li>
            </ul>
          </div>

          <div className="premium-panel report-panel export-panel">
            <div>
              <span className="soft-badge">Export Section</span>
              <h3>Download Report</h3>
              <p>Export the selected month expense report as an Excel file.</p>
            </div>

            <button type="button" className="glow-button" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
